import { memo } from "react"
import {
	IconArrowRotateClockwiseOutline18,
	IconCheckOutline18,
	IconEnvelopeCheckOutline18,
	IconUserOutline18,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { ReportingQueryProps } from "../reporting-query"
import {
	categoricalColor,
	formatDurationHours,
	reportingCardClassName,
	ReportingDonutChart,
	ReportingKpiCard,
} from "@/components/reporting/charts"
import { api } from "@/lib/api"

export const TeamTab = memo((props: ReportingQueryProps) => {
	const { startDate, endDate, ...filters } = props
	const query = api.workspace.getReportingTeam.useQuery(
		{ startDate, endDate, ...filters },
		{ cacheTime: Infinity, staleTime: 60_000 },
	)
	const data = query.data
	const loading = query.isLoading
	const agents = data?.agents ?? []
	const unassignedCount = data?.unassignedCount ?? 0

	const shareSlices = [
		...agents.map((agent, index) => ({
			id: agent.id,
			name: agent.name,
			value: agent.assigned,
			color: categoricalColor(index),
		})),
		...(unassignedCount > 0
			? [
					{
						id: "unassigned",
						name: "Unassigned",
						value: unassignedCount,
						color: categoricalColor(agents.length),
					},
				]
			: []),
	]

	return (
		<div className="space-y-4 p-4 sm:p-6">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<ReportingKpiCard
					label="Assigned"
					value={data?.totals.assigned}
					isLoading={loading}
					Icon={IconUserOutline18}
					tone="categorical-0"
					hint="Threads with an owner"
				/>
				<ReportingKpiCard
					label="Replied"
					value={data?.totals.replied}
					isLoading={loading}
					Icon={IconEnvelopeCheckOutline18}
					tone="categorical-3"
					hint="Assigned threads with a reply"
				/>
				<ReportingKpiCard
					label="Closed"
					value={data?.totals.closed}
					isLoading={loading}
					Icon={IconCheckOutline18}
					tone="categorical-1"
					hint="Assigned threads marked done"
				/>
				<ReportingKpiCard
					label="Reassigns"
					value={data?.reassignCount}
					isLoading={loading}
					Icon={IconArrowRotateClockwiseOutline18}
					tone="categorical-2"
					hint="Assignee change events"
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
				<div
					className={cn(
						reportingCardClassName,
						"overflow-x-auto xl:col-span-2",
					)}
				>
					<div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700/80">
						<h3 className="text-sm font-medium text-secondary">
							Teammate performance
						</h3>
					</div>
					<table className="min-w-full text-left text-sm">
						<thead className="border-b border-gray-200 text-xs text-tertiary dark:border-gray-700/80">
							<tr>
								<th className="px-4 py-3 font-medium">Teammate</th>
								<th className="px-4 py-3 font-medium">Assigned</th>
								<th className="px-4 py-3 font-medium">Replied</th>
								<th className="px-4 py-3 font-medium">Closed</th>
								<th className="px-4 py-3 font-medium">Median TTFR</th>
								<th className="px-4 py-3 font-medium">Time to assign</th>
								<th className="px-4 py-3 font-medium">Assign → close</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td className="px-4 py-6 text-tertiary" colSpan={7}>
										Loading…
									</td>
								</tr>
							) : agents.length === 0 ? (
								<tr>
									<td className="px-4 py-6 text-tertiary" colSpan={7}>
										No assigned conversations in this range
									</td>
								</tr>
							) : (
								agents.map((agent, index) => (
									<tr
										key={agent.id}
										className="border-b border-gray-100 last:border-b-0 dark:border-gray-800"
									>
										<td className="px-4 py-3">
											<div className="flex items-center gap-2 font-medium text-primary">
												<span
													className="h-2.5 w-2.5 shrink-0 rounded-full"
													style={{
														backgroundColor: categoricalColor(index),
													}}
												/>
												{agent.name}
											</div>
										</td>
										<td className="px-4 py-3">{agent.assigned}</td>
										<td className="px-4 py-3">{agent.replied}</td>
										<td className="px-4 py-3">{agent.closed}</td>
										<td className="px-4 py-3">
											{agent.ttfrHours != null
												? formatDurationHours(agent.ttfrHours)
												: "-"}
										</td>
										<td className="px-4 py-3">
											{agent.timeToAssignHours != null
												? formatDurationHours(agent.timeToAssignHours)
												: "-"}
										</td>
										<td className="px-4 py-3">
											{agent.assignToCloseHours != null
												? formatDurationHours(agent.assignToCloseHours)
												: "-"}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				<ReportingDonutChart
					title="Assigned share"
					description="Current assignee share for conversations created in the selected range."
					data={shareSlices}
					isLoading={loading}
				/>
			</div>
		</div>
	)
})
